//#region Imports Packages and Libs

import { store } from "../store/store";
import {
  STORE_MUTATION,
  type TypeLayout,
  type TypeStructure,
  type TypeComponentName,
  MESSAGE_COMPONENT_TYPE,
  type TypeImagePosition,
  type TypeObjectFit,
} from "../enums";
import { Component, Vue, Prop } from "vue-facing-decorator";
import type {
  IComponent,
  IComponentStyle,
  IDynamicButtonItem,
  IDynamicButtonMap,
  IModelMessageSettings,
  MessageStructureComponent,
} from "../types";
import ContainerTag from "../components/container-tag.vue";
import Button from "../components/button.vue";
import { CAROUSEL_RADIUS } from "../constant";
import * as DynamicButtonManager from "../helpers/carousel/dynamic_button_manager";

//#endregion

//#region Define the component in class-style

@Component({
  components: {
    ContainerTag,
    Button,
  },
})
export default class ListEvent extends Vue {
  /** Component data and configuration properties */
  @Prop({
    type: String,
    default: "bubble",
  })
  structure?: TypeStructure;

  @Prop({
    type: String,
    default: "carousel",
  })
  layout?: TypeLayout;

  @Prop({ type: Object, default: () => ({}) })
  component?: IComponent;

  @Prop({ type: Object, default: () => ({}) })
  settings?: IModelMessageSettings;

  /** Button element properties */
  get button1() {
    return this.component.button1;
  }

  get button2() {
    return this.component.button2;
  }

  get button3() {
    return this.component.button3;
  }

  /** Header image properties */
  get headerImage() {
    return this.component.headerImage;
  }

  /** Text elements */
  get html() {
    return this.component.html;
  }

  get line1() {
    return this.component.line1;
  }

  get line2() {
    return this.component.line2;
  }

  get tag() {
    return this.component.tag;
  }

  /** List item properties */
  get listItem() {
    return this.component.listItem;
  }

  /**
   * Retrieves the style object for a specific component name.
   * @param styleName - The name of the component style to retrieve.
   * @returns A style object containing alignment, font size, font weight, and max lines.
   */
  getElementStyle(element: TypeComponentName) {
    let style: IComponentStyle = {};
    const elementStyle = this.componentStyle[element];
    if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) style = elementStyle;

    const { textAlign, fontSize, fontWeight, maxLines, color, backgroundColor, margin, padding, background } = style;

    return {
      textAlign,
      fontSize,
      fontWeight,
      color,
      background: background || backgroundColor,
      maxLines,
      margin,
      padding,
      WebkitLineClamp: maxLines,
      lineClamp: maxLines,
    };
  }

  /**
   * Computes and returns the style object for the container element.
   *
   * This method extracts style properties from the `carouselItem` key of `componentStyle`,
   * ensuring that only valid object properties are used.
   * - Defaults backgroundColor to '#FFF'
   * - Defaults borderRadius to 'CAROUSEL_RADIUS'
   *
   * @returns {IComponentStyle} An object containing style properties such as `backgroundColor`,
   * `borderRadius`, `margin`, `padding`, `height`, `maxHeight`, `width`, and `maxWidth`.
   */
  get styleContainer() {
    let style: IComponentStyle = { backgroundColor: "#FFF", borderRadius: CAROUSEL_RADIUS };
    const elementStyle = this.componentStyle["carouselItem"];
    if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) {
      const {
        backgroundColor = "#FFF",
        background,
        borderRadius = CAROUSEL_RADIUS,
        margin,
        padding,
        height,
        maxHeight,
        width,
        maxWidth,
      } = elementStyle;

      style = {
        background: background || backgroundColor,
        borderRadius,
        margin,
        padding,
        height,
        maxHeight,
        width,
        maxWidth,
      };
    }

    return style;
  }

  /**
   * Retrieves the component style object for a given element name.
   * @param element - The name of the element.
   * @returns The style object for the specified element.
   */
  getComponentStyle(element: TypeComponentName) {
    let style: IComponentStyle = {};
    const elementStyle = this.componentStyle[element];
    if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) style = elementStyle;
    return style;
  }

  /**
   * Computes and returns the overall style configuration for the component.
   */
  get componentStyle() {
    if (
      this.settings.carouselSettings &&
      this.settings.carouselSettings.containerSettings &&
      this.settings.carouselSettings.containerSettings.componentItems
    ) {
      return this.settings.carouselSettings.containerSettings.componentItems;
    }
    return {};
  }

  // Getters for specific styles
  get styleHeaderImage(): IComponentStyle {
    return this.getComponentStyle("headerImage");
  }

  get styleLine1() {
    return this.getElementStyle("line1");
  }

  get styleLine2() {
    return this.getElementStyle("line2");
  }

  get styleHtml() {
    return this.getElementStyle("html");
  }

  get styleButton1() {
    return this.getElementStyle("button1");
  }

  get styleButton2() {
    return this.getElementStyle("button2");
  }

  get styleButton3() {
    return this.getElementStyle("button3");
  }

  get styleEllipsis() {
    return this.getElementStyle("ellipsis");
  }

  /**
   * JIRA: SD-7490
   * Computes and returns the final style object for the tag element.
   *
   * The method merges styles from `getComponentStyle("tag")` with fallback defaults,
   * especially for `borderRadius`, based on the component structure, image position,
   * and image availability.
   *
   * @returns {IComponentStyle} A complete style object for the tag element.
   *
   * ## Logic Summary:
   * - Uses `this.imgBorderRadius` by default for `borderRadius`
   * - If structure is `"card"` with `"top-middle"` image position or no image URL,
   *   it uses `this.styleContainer.borderRadius` or a fallback (`CAROUSEL_RADIUS`)
   * - Adjusts default `borderRadius` based on the tag's `position`:
   *   - `"top-left"` or `"bottom-right"` → `${borderRadius} 0`
   *   - Otherwise → `0 ${borderRadius}`
   * - Merges `tagComponentStyle` with computed `borderRadius` (unless already set)
   */
  get styleTag(): IComponentStyle {
    let borderRadius = this.imgBorderRadius;
    const tagComponentStyle = this.getComponentStyle("tag");

    if ((this.structure === "card" && this.imgPosition === "top-middle") || !this.imageUrl) {
      borderRadius = this.styleContainer.borderRadius || CAROUSEL_RADIUS;
    }

    let defaultBorderRadius = `0 ${borderRadius}`;

    if (tagComponentStyle.position === "top-left" || tagComponentStyle.position === "bottom-right") {
      defaultBorderRadius = `${borderRadius} 0`;
    }

    return {
      ...tagComponentStyle,
      borderRadius: tagComponentStyle.borderRadius || defaultBorderRadius,
    };
  }

  get tagContent() {
    return this.tag && this.tag.content;
  }

  /**
   * Determines and returns the visibility flags for different tag types based on tag content and orientation.
   *
   * This method evaluates whether a tag should be shown (`showTag`) and sets the appropriate flags in the
   * returned `IModelToggleTag` object, depending on the `tagOrientation` from `styleTag`.
   *
   * ## Logic Summary:
   * - Checks if the tag has content (`this.tag.content`)
   * - Based on `styleTag.tagOrientation`, sets one of the following:
   *   - `isButtonTag`: true if orientation is `"button"`
   *   - `isContentTag`: true if orientation is `"content"`
   *   - `isCardTag`: true if orientation is `"card"` or fallback when no image is present
   *   - `isImageTag`: true if orientation is anything else and `imageUrl` is available
   */
  get toggleTag() {
    const tagVisibility = {
      isButtonTag: false,
      isCardTag: false,
      isContentTag: false,
      isImageTag: false,
    };

    if (this.tagContent) {
      switch (this.styleTag.tagOrientation) {
        case "button":
          tagVisibility.isButtonTag = true;
          break;

        case "content":
          tagVisibility.isContentTag = true;
          break;

        case "card":
          tagVisibility.isCardTag = true;
          break;

        default:
          if (this.imageUrl) {
            tagVisibility.isImageTag = true;
          } else {
            tagVisibility.isCardTag = true;
          }
          break;
      }
    }

    return tagVisibility;
  }

  get isCarousel() {
    return this.layout === "carousel";
  }

  /**
   * Checks if the header image is positioned in the "top-middle".
   */
  get isTopMiddle(): boolean {
    return this.imgPosition === "top-middle";
  }
  /**
   * Checks if at least one button is available to be shown.
   * Includes both fixed buttons (button1, button2, button3) and dynamic buttons.
   */
  get showButton(): boolean {
    const hasFixedButtons = !!this.component.button1 || !!this.component.button2 || !!this.component.button3;
    const hasDynamicButtons = this.allDynamicButtons.length > 0;
    return this.isCarousel && (hasFixedButtons || hasDynamicButtons);
  }

  /**
   * Retrieves the URL of the header image. Falls back to a placeholder if the URL is not defined.
   * @returns {string} The image URL or "undefined" if no URL is set.
   */
  get imageUrl(): string {
    // let url = `${store.state.AWSServerURL}${store.state.AWSImageLogoPath}Carousel/Placeholder.png`;
    let url = ``;
    if (this.headerImage && this.headerImage.imageUrl) url = this.headerImage.imageUrl;
    else if (this.styleHeaderImage && this.styleHeaderImage.imageUrlPlaceholder)
      url = this.styleHeaderImage.imageUrlPlaceholder;
    return url;
  }

  /**
   * Retrieves the alt text for the header image. Falls back to a placeholder if not defined.
   * @returns {string} The alt text of the header image or an empty string if not set.
   */
  get imageAlt(): string {
    let alt = "IMAGE";
    if (this.headerImage && this.headerImage.alt) alt = this.headerImage.alt;
    else if (this.styleHeaderImage && this.styleHeaderImage.imageAltPlaceholder)
      alt = this.styleHeaderImage.imageAltPlaceholder;
    return alt;
  }

  /**
   * JIRA: SD-7369
   * Gets the aspect-ratio style for the header image.
   *
   * @returns {string | undefined} The aspect-ratio value, defaulting to null if not specified.
   */
  get imgAspectRatio(): string | undefined {
    let aspectRatio: string | undefined = undefined;
    if (this.styleHeaderImage && this.styleHeaderImage.aspectRatio) {
      aspectRatio = this.styleHeaderImage.aspectRatio;
    }
    return aspectRatio;
  }

  /**
   * Retrieves the width of the header image from its style.
   * @returns {string | undefined} The width of the header image or an empty string if not set.
   */
  get imgWidth(): string | undefined {
    let width: string | undefined = this.isTopMiddle ? "100%" : "100px";
    if (this.styleHeaderImage && this.styleHeaderImage.width) {
      /**
       * JIRA: SD-7369
       * Remove the width if image has width and aspectRatio
       */
      if (this.imgAspectRatio && this.styleHeaderImage.height) {
        width = undefined;
      } else if (this.styleHeaderImage.width) {
        width = this.styleHeaderImage.width;
      }
    }
    return width;
  }

  /**
   * Retrieves the height of the header image from its style.
   * @returns {string | null} The height of the header image or an empty string if not set.
   */
  get imgHeight(): string | undefined {
    let height: string | undefined = this.isTopMiddle ? "auto" : "100px";
    if (this.styleHeaderImage) {
      /**
       * JIRA: SD-7369
       * Remove the height if image has width and aspectRatio
       */
      if (this.imgAspectRatio && this.styleHeaderImage.width) {
        height = undefined;
      } else if (this.styleHeaderImage.height) {
        height = this.styleHeaderImage.height;
      }
    }
    return height;
  }

  /**
   * Retrieves the position of the header image from its style. Defaults to "top-middle".
   * @returns {TypeImagePosition} The position of the header image.
   */
  get imgPosition(): TypeImagePosition {
    let position: TypeImagePosition = "top-middle";
    if (this.styleHeaderImage && this.styleHeaderImage.position) {
      position = this.styleHeaderImage.position as TypeImagePosition;
    }
    return position;
  }

  /**
   * JIRA: SD-7369
   * Gets the object-fit style for the header image.
   *
   * @returns {TypeObjectFit | undefined} The object-fit value, defaulting to "none" if not specified.
   */
  get imgObjectFit(): TypeObjectFit | undefined {
    let objectFit: TypeObjectFit | undefined = undefined;
    if (this.styleHeaderImage && this.styleHeaderImage.objectFit) {
      objectFit = this.styleHeaderImage.objectFit as TypeObjectFit;
    }
    return objectFit;
  }

  /**
   * Computes and returns the border-radius value for the header image.
   *
   * - If `structure` is `"card"`, the border-radius is set to `"0"`.
   * - Otherwise, if `styleHeaderImage` has a `borderRadius` property, it uses that value.
   * - Defaults to `CAROUSEL_RADIUS` if no other conditions are met.
   *
   * @returns {string} The computed border-radius value for the image.
   */
  get imgBorderRadius(): string {
    let borderRadius = CAROUSEL_RADIUS;

    if (this.structure === "card") {
      borderRadius = "0";
    } else if (this.styleHeaderImage && this.styleHeaderImage.borderRadius) {
      borderRadius = this.styleHeaderImage.borderRadius;
    }

    return borderRadius;
  }

  /**
   * Retrieves the button properties for a specific component.
   * @param element - The name of the button element.
   */
  getButtonProps(element: TypeComponentName): MessageStructureComponent {
    return {
      ...this.component[element],
      type:
        this.componentStyle[element] && this.componentStyle[element].type
          ? (this.componentStyle[element].type as MESSAGE_COMPONENT_TYPE)
          : MESSAGE_COMPONENT_TYPE.Button,
    };
  }

  // Getters for individual button components
  get button1Component(): MessageStructureComponent {
    return this.getButtonProps("button1");
  }

  get button2Component(): MessageStructureComponent {
    return this.getButtonProps("button2");
  }

  get button3Component(): MessageStructureComponent {
    return this.getButtonProps("button3");
  }

  /**
   * Displays a modal for the carousel detail.
   */
  showModal() {
    store.commit(STORE_MUTATION.UpdateCarouselModalDetail, {
      component: this.component,
      settings: this.settings,
      layout: this.layout,
      structure: this.structure,
    });
  }

  /**
   * Modifies the style of the carousel based on its layout and structure.
   */
  modifyCarouselStyle() {
    try {
      const componentWrapper = this.$refs.componentWrapper as HTMLDivElement;
      const topWrapper = this.$refs.topWrapper as HTMLDivElement;
      const contentWrapper = this.$refs.contentWrapper as HTMLDivElement;
      const buttonWrapper = this.$refs.buttonWrapper as HTMLDivElement;
      const imageEvent = this.$refs.imageEvent as HTMLImageElement;
      const customEllipsis = this.$refs.custom_ellipsis as HTMLButtonElement;
      const tagContent = this.$refs.tagContent as HTMLDivElement;
      const tagButton = this.$refs.tagButton as HTMLDivElement;

      const containerPadding = this.styleContainer.padding || "12px";

      /**
       * JIRA: SD-7377
       *
       * Determines whether the "Show More" functionality is enabled.
       * If `this.component.enableShowMore` is explicitly set as a boolean, it uses that value;
       * otherwise, it defaults to `true`.
       *
       */
      const enableShowMore = typeof this.component.enableShowMore === "boolean" ? this.component.enableShowMore : true;

      /**
       * JIRA: SD-7377
       *
       * Handles the custom ellipsis display logic.
       * If a custom ellipsis element is provided, it retrieves the ellipsis configuration from
       * the component's style and updates the display and content accordingly.
       */
      if (customEllipsis) {
        const ellipsisConfig = this.getComponentStyle("ellipsis");

        // Show or hide the ellipsis based on the enableShowMore flag
        customEllipsis.style.display = enableShowMore ? "inline" : "none";

        // Set the inner text of the ellipsis element
        customEllipsis.innerText = ellipsisConfig && ellipsisConfig.content ? ellipsisConfig.content : "Show More";
      }

      if (!this.isCarousel && componentWrapper) {
        componentWrapper.style.flexDirection = "column";
        componentWrapper.style.position = "relative";
        componentWrapper.style.width = "auto";
        componentWrapper.style.height = "auto";
      }

      switch (this.imgPosition) {
        case "top-middle":
          if (componentWrapper) {
            componentWrapper.style.flexDirection = "column";
          }

          if (!this.isCarousel || this.structure === "card") {
            if (imageEvent) {
              imageEvent.style.width = "100% !important";
            }
          }
          break;

        case "left-top":
          if (componentWrapper) {
            componentWrapper.style.alignItems = "flex-start";
          }
          break;

        case "left-middle":
          if (topWrapper) {
            topWrapper.style.alignItems = "center";
          }
          break;

        case "right-middle":
          if (componentWrapper) {
            componentWrapper.style.alignItems = "center";
          }

          if (topWrapper) {
            topWrapper.style.flexDirection = "row-reverse";
            topWrapper.style.alignItems = "center";
          }
          break;

        case "right-top":
          if (topWrapper) {
            topWrapper.style.flexDirection = "row-reverse";
            topWrapper.style.alignItems = "flex-start";
          }
          break;
      }

      switch (this.structure) {
        case "bubble":
          if (componentWrapper) {
            componentWrapper.style.padding = containerPadding;
          }
          break;

        case "card":
          if (componentWrapper) {
            componentWrapper.style.gap = "0";
          }

          if (contentWrapper) {
            contentWrapper.style.padding = containerPadding;
          }

          if (buttonWrapper) {
            buttonWrapper.style.padding = containerPadding;
          }

          if (this.imgPosition !== "left-middle") {
            if (topWrapper) {
              topWrapper.style.gap = "4px !important";
              topWrapper.style.width = "100% !important";
            }
          }
          break;

        case "modal":
          if (componentWrapper) {
            componentWrapper.style.border = "1px solid #ccc";
            componentWrapper.style.padding = containerPadding;
          }
          break;
      }

      if (!this.imageUrl) {
        if (componentWrapper) {
          componentWrapper.style.borderRadius = this.styleContainer.borderRadius || CAROUSEL_RADIUS;
        }

        if (contentWrapper) {
          contentWrapper.style.marginTop = "4px";
        }
      }

      /**
       * JIRA: SD-7490
       * Sets the `flexDirection` style on the tag container elements (`tagContent` or `tagButton`)
       * based on the tag's orientation and position.
       *
       * The direction is determined by the `styleTag.position`:
       * - `"bottom-left"` or `"bottom-right"` → `"column-reverse"`
       * - All other positions → `"column"`
       *
       * The computed direction is then applied to:
       * - `tagContent` if the tag orientation is `"content"`
       * - `tagButton` if the tag orientation is `"button"`
       *
       * @remarks This ensures the tag's layout respects the visual hierarchy depending on its placement.
       */
      const tagOrientation = this.styleTag.tagOrientation;
      const tagPosition = this.styleTag.position;
      const flexDirection =
        tagPosition === "bottom-left" || tagPosition === "bottom-right" ? "column-reverse" : "column";
      if (tagContent && tagOrientation === "content") {
        tagContent.style.flexDirection = flexDirection;
      }

      if (tagButton && tagOrientation === "button") {
        tagButton.style.flexDirection = flexDirection;
      }
    } catch (error) {
      console.error("Error in modify the carousel in 'list-event.ts -> modifyCarouselStyle': ", error);
    }
  }

  /**
   * JIRA: SD-6998
   * Handles the button click event.
   * Closes the carousel modal (detail or list) by updating the Vuex store state.
   */
  onClickButton() {
    if (store.state.carouselModalDetail) {
      store.commit(STORE_MUTATION.UpdateCarouselModalDetail, null);
    }

    if (store.state.carouselModalList) {
      store.commit(STORE_MUTATION.UpdateCarouselModalList, null);
    }
  }

  /**
   * Lifecycle hook called after the component is mounted.
   * Initializes the carousel styles and content adjustments.
   */
  mounted() {
    this.modifyCarouselStyle();
  }

  //#region Support Dynamic buttons

  /**
   * Gets all dynamic buttons from the component's buttons object.
   * Filters and transforms valid button data into renderable button items.
   *
   * @returns Array of dynamic button items with key, component, and style
   */
  get allDynamicButtons(): IDynamicButtonItem[] {
    try {
      const buttonsObject = this.getValidButtonsObject();
      if (!buttonsObject) return [];

      // Transform buttons with access to component style
      return DynamicButtonManager.transformButtonsToItems(buttonsObject, this.componentStyle);
    } catch (ex) {
      console.error("[list-event.ts] An error has occurred \n-> Inside allDynamicButtons -> Error: ", ex);
      return [];
    }
  }

  /**
   * Validates and returns the buttons object from component if it exists and is valid
   *
   * @returns The buttons object or null if invalid
   */
  getValidButtonsObject(): IDynamicButtonMap | null {
    const buttonsObj = this.component?.buttons;

    if (!DynamicButtonManager.isValidButtonsObject(buttonsObj)) {
      return null;
    }

    return buttonsObj;
  }

  //#endregion
}

//#endregion
